import { useMemo } from 'react';
import {
  ExitToApp,
  AccountCircle,
  PeopleAltOutlined,
  AccountTreeOutlined,
  QuestionAnswerOutlined,
  AddIcCall,
  AccountBalance,
  MenuBookOutlined,
} from '@mui/icons-material';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import SchemaIcon from '@mui/icons-material/Schema';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';

import {
  AgentsPage,
  AgentPage,
  NewAgentPage,
  SipTrunkPage,
  OrganizationPage,
  ConversationsPage,
  RolesAndPermissionsPage,
  UsersAndOrganizationsPage,
  KnowledgeManagementPage,
  SchemaEditorPage,
  CallManagementPage,
  ModelsManagementPage,
  KnowledgeSourceList,
  BatchCallListPage,
  CallTaskWizardPage,
  CallsManagementPage,
  AgentsBillingPage,
  ConversationsBillingPage,
  ConversationBillingDetailsPage,
  PromptLibraryPage,
  SipTrunkVendorsPage,
  AnalyticsPage,
  ConversationPage,
} from '../../pages';
import { MenuLabel, ROUTE_PATTERN } from './const';
import type { MenuItem } from './Layout';
import {
  AnalyticsIcon,
  BatchCallIcon,
  FolderIcon,
  RetryCallIcon,
  UserGroupIcon,
} from '../../assets/icons';
import { PERMISSIONS } from '../../shared/constants/permissions';
import LiveKitServerPage from '../../pages/livekit-server';

export const getAuthenticatedMenuItems = (logout: Function): MenuItem[] => [
  {
    menuTitle: MenuLabel.Analytics,
    pageTitle: 'Analytics',

    url: ROUTE_PATTERN.analytics.list,
    icon: <AnalyticsIcon />,
    component: AnalyticsPage,
    action: [
      PERMISSIONS.VIEW_DASHBOARD_ANALYTICS,
      PERMISSIONS.VIEW_WIDGET_ANALYTICS,
    ],
  },
  {
    menuTitle: MenuLabel.ManageAgents,
    pageTitle: 'Agents List',

    url: ROUTE_PATTERN.agents.list,
    icon: <PeopleAltOutlined />,
    component: AgentsPage,
    action: PERMISSIONS.VIEW_AGENTS,
  },
  {
    menuTitle: MenuLabel.Conversations,
    pageTitle: 'Conversations List',

    url: ROUTE_PATTERN.conversation.list,
    icon: <QuestionAnswerOutlined />,
    component: ConversationsPage,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    pageTitle: 'Conversation',

    url: ROUTE_PATTERN.conversation.detail,
    component: ConversationPage,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    pageTitle: 'Create new Agent',

    url: ROUTE_PATTERN.agents.new,
    component: NewAgentPage,
    action: PERMISSIONS.CREATE_AGENTS,
  },
  {
    pageTitle: 'Agent Configuration',

    url: ROUTE_PATTERN.agents.detail,
    icon: <AccountCircle />,
    component: AgentPage,
    action: PERMISSIONS.VIEW_AGENTS,
  },
  {
    menuTitle: MenuLabel.KnowledgeManagement,
    pageTitle: 'Knowledge Management',

    url: ROUTE_PATTERN.knowledgeManagement.list,
    icon: <FolderIcon />,
    component: KnowledgeManagementPage,
    action: [PERMISSIONS.MANAGE_KNOWLEDGE, PERMISSIONS.MANAGE_SCRAPER],
  },
  {
    pageTitle: 'Knowledge Details',

    url: ROUTE_PATTERN.knowledgeManagement.detail,
    component: KnowledgeSourceList,
    action: PERMISSIONS.MANAGE_KNOWLEDGE,
  },
  {
    pageTitle: 'Organization',

    url: ROUTE_PATTERN.orgManagement.organization,
    component: OrganizationPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.CallManagement,
    pageTitle: 'Call Management',

    url: ROUTE_PATTERN.callManagement.page,
    component: CallManagementPage,
    icon: <RetryCallIcon />,
    action: [PERMISSIONS.MAKE_CALL, PERMISSIONS.MAKE_CONFERENCE_CALL],
  },
  {
    menuTitle: MenuLabel.RoleManagement,

    url: ROUTE_PATTERN.roleManagement.page,
    icon: <AccountTreeOutlined />,
    component: RolesAndPermissionsPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.OrgManagement,

    url: ROUTE_PATTERN.orgManagement.list,
    icon: <UserGroupIcon />,
    component: UsersAndOrganizationsPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.JSONManagement,

    url: ROUTE_PATTERN.jsonManagement.page,
    component: SchemaEditorPage,
    icon: <SchemaIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.ModelsManagement,

    url: ROUTE_PATTERN.modelsManagement.page,
    component: ModelsManagementPage,
    icon: <DomainAddIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.SipTrunk,

    url: ROUTE_PATTERN.sipTrunk.page,
    icon: <AddIcCall />,
    component: SipTrunkPage,
    action: PERMISSIONS.MANAGE_SIP_TRUNK,
  },
  {
    menuTitle: MenuLabel.ConversationsBilling,

    url: ROUTE_PATTERN.billing.list,
    component: AgentsBillingPage,
    icon: <AccountBalance />,
    action: PERMISSIONS.VIEW_BILLING,
  },
  {
    menuTitle: MenuLabel.PromptLibrary,

    url: ROUTE_PATTERN.promptLibrary.page,
    component: PromptLibraryPage,
    icon: <MenuBookOutlined />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.SipTrunkVendors,
    pageTitle: 'SIP Trunk Vendors',

    url: ROUTE_PATTERN.sipTrunkVendors.page,
    component: SipTrunkVendorsPage,
    icon: <SettingsPhoneIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.LiveKitServer,

    url: ROUTE_PATTERN.livekitServer.page,
    component: LiveKitServerPage,
    icon: <AddIcCall />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    pageTitle: 'Conversations Billing',

    url: ROUTE_PATTERN.billing.agent,
    component: ConversationsBillingPage,
    action: PERMISSIONS.VIEW_BILLING,
  },
  {
    pageTitle: 'Conversation',
    url: ROUTE_PATTERN.billing.conversationDetail,
    component: ConversationBillingDetailsPage,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    menuTitle: MenuLabel.BatchCalls,
    pageTitle: 'Mass Outreach Tasks',

    url: ROUTE_PATTERN.batchCalls.list,
    component: BatchCallListPage,
    icon: <BatchCallIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    pageTitle: 'Mass Outreach Tasks',

    url: ROUTE_PATTERN.batchCalls.wizard,
    component: CallTaskWizardPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    url: ROUTE_PATTERN.batchCalls.wizardDetail,
    component: CallTaskWizardPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    url: ROUTE_PATTERN.callsManagement.detail,
    component: CallsManagementPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.Logout,

    onClick: () =>
      logout({ logoutParams: { returnTo: window.location.origin } }),
    icon: <ExitToApp />,
  },
];

export const getUnauthMenuItems = (loginWithRedirect: Function): MenuItem[] => [
  {
    menuTitle: MenuLabel.Login,
    onClick: () => loginWithRedirect(),
    icon: null,
  },
];

export const useRoutes = (
  login: Function,
  logout: Function,
  isAuthenticated: boolean
) => {
  const authMenuItems = useMemo(
    () => getAuthenticatedMenuItems(logout),
    [logout]
  );

  const unauthMenuItems = useMemo(() => getUnauthMenuItems(login), [login]);

  const menuItems = isAuthenticated ? authMenuItems : unauthMenuItems;

  const [publicRoutes, privateRoutes] = useMemo(() => {
    const itemsWithRoutes = menuItems.filter(i => i.component && i.url);

    return [
      itemsWithRoutes.filter(i => i.isPublic),
      itemsWithRoutes.filter(i => !i.isPublic),
    ];
  }, [menuItems]);

  return { publicRoutes, privateRoutes, menuItems };
};
